import React, { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from 'react';
import BackArrow from '../../BackArrow';
import './Scanner3.css';
import { host_url } from '../../App';
import storminiGif from '../../assets/Stormini_Titelbild.gif';

// Pure, testable helper: decide whether the dragged item should move into the
// hovered target slot, based on how far the pointer has entered that slot.
// A smaller enterRatio swaps sooner (more responsive); a larger one needs the
// pointer deeper into the neighbour before swapping (more deliberate).
export function shouldSwap(draggedIndex, targetIndex, clientX, slotLeft, slotWidth, enterRatio = 0.3) {
  if (
    draggedIndex < 0 || targetIndex < 0 ||
    draggedIndex === targetIndex ||
    !(slotWidth > 0)
  ) {
    return false;
  }
  if (draggedIndex < targetIndex) {
    // Moving right: swap once we're enterRatio into the target from its LEFT edge.
    return clientX > slotLeft + slotWidth * enterRatio;
  }
  // Moving left: swap once we're enterRatio into the target from its RIGHT edge.
  return clientX < slotLeft + slotWidth * (1 - enterRatio);
}

// Countdown unten rechts als eigene Komponente: der Sekunden-Tick rendert so
// nur dieses kleine Element neu statt der gesamten Scanner-Seite — auf den
// leistungsschwachen Terminal-Rechnern war der Voll-Rerender pro Sekunde spürbar.
function TimeoutCounter({ active, timerEndRef }) {
  const [remaining, setRemaining] = useState(null);

  useEffect(() => {
    if (!active) {
      setRemaining(null);
      return;
    }
    const tick = () => {
      if (!timerEndRef.current) {
        setRemaining(null);
        return;
      }
      const rest = Math.ceil((timerEndRef.current - Date.now()) / 1000);
      setRemaining(rest > 0 ? rest : 0);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [active, timerEndRef]);

  if (remaining === null) return null;
  return <div className="timeout-counter">{remaining}s</div>;
}

function Scanner() {
  // State
  const ANIMATION_DURATION_MS = 6000; // Dauer der GIF-Animation in Millisekunden anpassen!
  const [isPlaying, setIsPlaying] = useState(false);
  const [gifKey, setGifKey] = useState(Date.now());
  const [gifFirstFrame, setGifFirstFrame] = useState(null);
  const [scannedChild, setScannedChild] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [einheiten, setEinheiten] = useState([]);
  const [selectedEinheit, setSelectedEinheit] = useState('');

  const [filteredJobs, setFilteredJobs] = useState([]);
  const [selectedJobs, setSelectedJobs] = useState([]);

  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [draggedJob, setDraggedJob] = useState(null);

  const [page, setPage] = useState(1); // 1: Scan, 2: Bewerbung, 3: Erfolg

  const ghostRef = useRef(null);
  const ghostOffset = useRef({ x: 0, y: 0 });
  const containerRef = useRef(null);
  const firstRects = useRef(new Map());
  const inactivityTimerRef = useRef(null);
  const timerEndRef = useRef(null);
  const rafRef = useRef(null);
  const latestPointRef = useRef(null);
  // Identifier des Fingers, der den aktuellen Drag steuert. Nur dieser Finger
  // darf den Ghost bewegen/beenden — ein zweiter Finger (Handballen, zweites
  // Kind) darf weder einen neuen Drag starten noch den laufenden abbrechen.
  const touchIdRef = useRef(null);
  // Slot-Positionen werden einmal beim Drag-Start gemessen statt pro Frame:
  // die 5 Slots liegen im Sticky-Header und bewegen sich während des Drags
  // nicht — das spart auf schwachen Rechnern teure Layout-Abfragen pro Frame.
  const slotRectsRef = useRef(null);

  // Extract the first frame of the GIF once when the component mounts
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      setGifFirstFrame(canvas.toDataURL());
    };
    img.src = storminiGif;
  }, []);

  // FLIP technique to animate reordering smoothly.
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const children = Array.from(container.children).filter(c => c.dataset.jobId);
    const isDragged = (child) =>
      draggedJob && parseInt(child.dataset.jobId, 10) === draggedJob.JobID;

    // 1. Strip any leftover/in-flight FLIP transforms so we can read the TRUE
    //    natural positions (measuring while a transform is applied was the bug
    //    that caused stale positions and the jittery re-animations).
    children.forEach(child => {
      child.style.transition = 'none';
      child.style.transform = 'none';
    });
    container.getBoundingClientRect(); // single reflow to apply the reset

    // 2. Measure natural positions.
    const newRects = new Map();
    children.forEach(child => newRects.set(child.dataset.jobId, child.getBoundingClientRect()));

    // 3. Invert: jump each moved slot back to where it was.
    const moving = [];
    children.forEach(child => {
      if (isDragged(child)) {
        // Dragged placeholder must not animate; hand control back to CSS.
        child.style.transition = '';
        child.style.transform = '';
        return;
      }
      const firstRect = firstRects.current.get(child.dataset.jobId);
      const lastRect = newRects.get(child.dataset.jobId);
      if (firstRect) {
        const dx = firstRect.left - lastRect.left;
        const dy = firstRect.top - lastRect.top;
        if (dx || dy) {
          child.style.transform = `translate(${dx}px, ${dy}px)`;
          moving.push(child);
          return;
        }
      }
      // Not moving: restore CSS-driven transitions immediately.
      child.style.transition = '';
      child.style.transform = '';
    });

    // 4. Play: animate the moved slots from old -> natural, then clean up the
    //    inline styles so the slots' own CSS transitions resume.
    if (moving.length) {
      container.getBoundingClientRect(); // reflow with inverted transforms applied
      requestAnimationFrame(() => {
        moving.forEach(child => {
          child.style.transition = 'transform 200ms cubic-bezier(0.2, 0.8, 0.2, 1)';
          child.style.transform = 'none';
          child.addEventListener('transitionend', () => {
            child.style.transition = '';
            child.style.transform = '';
          }, { once: true });
        });
      });
    }

    // 5. Remember natural positions for the next cycle.
    firstRects.current = newRects;
  }, [selectedJobs, draggedJob]);

  // Einheiten laden — /einheiten_mit_zeiten liefert auch Start/Ende,
  // die wir für das automatische Bewerbungsfenster brauchen.
  // silent: beim Hintergrund-Polling soll ein transienter Netzwerkfehler
  // nicht die laufende Bewerbung auf die Fehlerseite werfen.
  const fetchEinheiten = useCallback((silent = false) => {
    return fetch(`${host_url}/einheiten_mit_zeiten`)
      .then(response => response.json())
      .then(data => {
        const list = Array.isArray(data) ? data : [];
        list.sort((a, b) => new Date(a.Datum) - new Date(b.Datum));
        setEinheiten(list);
      })
      .catch(error => {
        console.error('Error fetching einheiten:', error);
        if (!silent) {
          setError('Fehler beim Laden der Schichten. Bitte versuche es später erneut.');
        }
      });
  }, []);

  useEffect(() => {
    fetchEinheiten();
  }, [fetchEinheiten]);

  // Solange die Schicht-Auswahl offen ist, regelmäßig nachladen — so tauchen
  // vom Kernteam frisch aktivierte Schichten ohne Terminal-Refresh auf.
  useEffect(() => {
    if (page !== 2 || selectedEinheit !== '') return;
    const interval = setInterval(() => fetchEinheiten(true), 15000);
    return () => clearInterval(interval);
  }, [page, selectedEinheit, fetchEinheiten]);

  // Fetch jobs and existing wishes when an Einheit is selected
  useEffect(() => {
    if (selectedEinheit && scannedChild) {
      setIsLoading(true);
      // Reset selections for the new shift
      setSelectedJobs([]);
      setError(null);

      Promise.all([
        fetch(`${host_url}/jobs_verfuegbarkeiten?einheitId=${selectedEinheit}`),
        fetch(`${host_url}/kinderwuensche?kindId=${scannedChild.KindID}&einheitId=${selectedEinheit}`)
      ])
      .then(async ([jobsResponse, wishesResponse]) => {
        if (!jobsResponse.ok) {
          throw new Error('Fehler beim Laden der Jobdaten.');
        }
        if (!wishesResponse.ok) {
          // It's okay if wishes don't exist, so we don't throw an error here.
          console.log('Keine existierenden Wünsche gefunden.');
        }

        const jobsData = await jobsResponse.json();
        const wishesData = wishesResponse.ok ? await wishesResponse.json() : [];

        // Process jobs
        const currentEinheit = einheiten.find(e => e.ID === selectedEinheit);
        const isAfternoon = currentEinheit && currentEinheit.Name && currentEinheit.Name.endsWith("Nachmittag");

        const available = jobsData.filter(job => {
          if (job.Slots <= 0) return false;
          if (isAfternoon && job.Ganztagesjob) return false;
          return true;
        });
        available.sort((a, b) => a.JobName.localeCompare(b.JobName));
        setFilteredJobs(available);

        // Process wishes if they exist
        if (wishesData && wishesData.length > 0) {
          // Sort wishes by priority (descending, as 50 is highest)
          wishesData.sort((a, b) => b.Prioritaet - a.Prioritaet);

          const preSelectedJobs = wishesData.map(wish => {
            // Find the full job object from the available jobs
            return available.find(job => job.JobID === wish.JobID);
          }).filter(Boolean); // Filter out any undefined jobs if a wish refers to a now-unavailable job

          setSelectedJobs(preSelectedJobs);
        }
      })
      .catch(error => {
        console.error('Error fetching data for einheit:', error);
        setError(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
    } else {
      setFilteredJobs([]);
    }
  }, [selectedEinheit, scannedChild, einheiten]);

  // Barcode processing logic
  const processScan = useCallback(async (scannedString) => {
    setIsLoading(true);
    setError(null);

    // Der Barcode ist die reine Kind-ID (nur Ziffern).
    // Scanner hängen manchmal unsichtbare Steuerzeichen an, deshalb filtern wir alles außer Ziffern.
    const id = String(scannedString).replace(/\D/g, '');
    
    if (!id) {
      setError("Ungültiger Barcode. Erwartet wird eine Zahl (Kind-ID).");
      setIsLoading(false);
      return;
    }

    try {
      // Einheiten parallel frisch laden — das Aktiv-Flag kann sich seit dem
      // letzten Abruf geändert haben (Kernteam schaltet Schichten frei).
      const [childResponse] = await Promise.all([
        fetch(`${host_url}/kinder?kindId=${id}`),
        fetchEinheiten(),
      ]);
      if (!childResponse.ok) throw new Error(`Kind mit ID ${id} nicht gefunden.`);
      const childData = await childResponse.json();

      if (childData) {
        setScannedChild(childData);
        setPage(2); // Go to application page
      } else {
        throw new Error(`Kind mit ID ${id} nicht gefunden.`);
      }

    } catch (e) {
      setError(e.message);
      setScannedChild(null);
    } finally {
      setIsLoading(false);
    }
  }, [fetchEinheiten]);

  // Barcode scanner listener
  useEffect(() => {
    if (page !== 1 || isLoading) {
      return;
    }

    let barcode = '';
    const handleKeyDown = (event) => {
      const target = event.target;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      ) {
        return;
      }

      if (event.key === 'Enter') {
        if (barcode.length > 0) processScan(barcode);
        barcode = '';
      } else if (event.key.length === 1) {
        barcode += event.key;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [page, isLoading, processScan]);

  // Auto-reset: 3min inactivity on page 2, 15s auto-reset on page 3
  useEffect(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    timerEndRef.current = null;

    if (page === 2) {
      const duration = 3 * 60 * 1000;
      timerEndRef.current = Date.now() + duration;
      inactivityTimerRef.current = setTimeout(performReset, duration);
    } else if (page === 3) {
      const duration = 15 * 1000;
      timerEndRef.current = Date.now() + duration;
      inactivityTimerRef.current = setTimeout(performReset, duration);
    }

    return () => {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // Handlers

  // Sicherheitsnetz gegen "schwebende" Jobs: entfernt ALLE Ghost-Klone im
  // Dokument, nicht nur den in ghostRef getrackten. Verwaiste Klone (z. B.
  // durch Multi-Touch) blieben sonst bis zum Seiten-Reload im Vordergrund.
  const removeOrphanGhosts = (keep = null) => {
    document.querySelectorAll('.touch-drag-ghost').forEach(el => {
      if (el !== keep && el.parentNode) el.parentNode.removeChild(el);
    });
  };

  const performReset = () => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    timerEndRef.current = null;
    // Tear down any in-flight drag so a reset/timeout never leaves a stray ghost.
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    ghostRef.current = null;
    touchIdRef.current = null;
    slotRectsRef.current = null;
    removeOrphanGhosts();
    latestPointRef.current = null;
    setDraggedJob(null);
    setScannedChild(null);
    setError(null);
    setSelectedEinheit('');
    setSelectedJobs([]);
    setPage(1);
    setShowCancelConfirm(false);
  };

  const handleLogout = performReset;

  // Nach dem Absenden eingeloggt bleiben (scannedChild behalten) und zurück
  // zur Schicht-Auswahl für eine weitere Bewerbung.
  const handleNextApplication = () => {
    fetchEinheiten(true);
    setSelectedEinheit('');
    setSelectedJobs([]);
    setError(null);
    setPage(2);
  };

  const startOrResetInactivityTimer = () => {
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    const duration = 3 * 60 * 1000;
    timerEndRef.current = Date.now() + duration;
    inactivityTimerRef.current = setTimeout(performReset, duration);
  };

  const handleJobSelect = (job) => {
    const isSelected = selectedJobs.some(selected => selected.JobID === job.JobID);

    if (isSelected) {
      // Remove job from selection
      setSelectedJobs(current => current.filter(selected => selected.JobID !== job.JobID));
    } else {
      // Add job to selection if not at max
      if (selectedJobs.length < 5) {
        setSelectedJobs(current => [...current, job]);
      }
      // Optional: else show a message that max is reached
    }
  };

  // Shared reorder logic. Uses shouldSwap() so a swap only commits once the
  // pointer has entered the neighbour by enterRatio (responsive, low flicker).
  const reorderByPoint = (clientX, targetJobId, targetRect) => {
    if (!draggedJob || targetJobId === draggedJob.JobID) return;

    setSelectedJobs(current => {
      const draggedIndex = current.findIndex(j => j.JobID === draggedJob.JobID);
      const targetIndex = current.findIndex(j => j.JobID === targetJobId);
      if (!shouldSwap(draggedIndex, targetIndex, clientX, targetRect.left, targetRect.width)) {
        return current;
      }
      const next = [...current];
      const [moved] = next.splice(draggedIndex, 1);
      next.splice(targetIndex, 0, moved);
      return next;
    });
  };

  // Touch-Variante: Ziel ist ein Slot-INDEX aus den beim Drag-Start gecachten
  // Rects; der Job an dieser Position wird aus dem aktuellen State gelesen.
  const reorderToSlot = (clientX, slotIndex, slotRect) => {
    if (!draggedJob) return;

    setSelectedJobs(current => {
      const targetJob = current[slotIndex];
      if (!targetJob || targetJob.JobID === draggedJob.JobID) return current;
      const draggedIndex = current.findIndex(j => j.JobID === draggedJob.JobID);
      if (!shouldSwap(draggedIndex, slotIndex, clientX, slotRect.left, slotRect.width)) {
        return current;
      }
      const next = [...current];
      const [moved] = next.splice(draggedIndex, 1);
      next.splice(slotIndex, 0, moved);
      return next;
    });
  };

  // Single teardown used by every drag-end path (drop, touchend, touchcancel,
  // dragend). Clears state immediately so a slot can never stay stuck in the
  // dragging placeholder state, then snaps the floating ghost (touch only)
  // into its final slot before removing it.
  const endDrag = () => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    latestPointRef.current = null;
    touchIdRef.current = null;
    slotRectsRef.current = null;

    const ghost = ghostRef.current;
    const job = draggedJob;
    ghostRef.current = null;
    setDraggedJob(null); // clear FIRST – guarantees no stuck/invisible slot

    removeOrphanGhosts(ghost); // sweep strays; keep the one we snap back below

    if (!ghost) return;

    const removeGhost = () => { if (ghost.parentNode) ghost.parentNode.removeChild(ghost); };
    const targetSlot = job && containerRef.current
      ? containerRef.current.querySelector(`.priority-slot[data-job-id="${job.JobID}"]`)
      : null;

    if (targetSlot) {
      const rect = targetSlot.getBoundingClientRect();
      ghost.style.transition = 'transform 160ms cubic-bezier(0.2, 0.8, 0.2, 1)';
      ghost.style.transform = `translate3d(${rect.left}px, ${rect.top}px, 0) scale(1)`;
      ghost.addEventListener('transitionend', removeGhost, { once: true });
      setTimeout(removeGhost, 220); // fallback if transitionend never fires
    } else {
      removeGhost();
    }
  };

  const handleDragStart = (e, job) => {
    setDraggedJob(job);
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDragEnter = (e, targetJob) => {
    e.preventDefault();
    if (!draggedJob || draggedJob.JobID === targetJob.JobID) return;
    reorderByPoint(e.clientX, targetJob.JobID, e.currentTarget.getBoundingClientRect());
  };

  const moveGhost = (clientX, clientY) => {
    if (!ghostRef.current) return;
    const x = clientX - ghostOffset.current.x;
    const y = clientY - ghostOffset.current.y;
    ghostRef.current.style.transform = `translate3d(${x}px, ${y}px, 0) scale(1.05)`;
  };

  const handleTouchStart = (e, job) => {
    // Läuft bereits ein Drag (zweiter Finger, Handballen, zweites Kind)?
    // Ignorieren — sonst überschreibt der neue Klon ghostRef und der alte
    // Ghost schwebt verwaist für immer im Vordergrund.
    if (draggedJob || ghostRef.current || touchIdRef.current !== null) return;
    removeOrphanGhosts();

    setDraggedJob(job);

    // changedTouches[0] ist der Finger, der DIESES Event ausgelöst hat —
    // touches[0] wäre bei bereits aufliegendem zweiten Finger der falsche.
    const touch = e.changedTouches[0];
    touchIdRef.current = touch.identifier;
    const targetElement = e.currentTarget;
    const rect = targetElement.getBoundingClientRect();

    // Slot-Geometrie einmalig cachen (Reihenfolge = Slot-Index 0..4).
    slotRectsRef.current = containerRef.current
      ? Array.from(containerRef.current.children)
          .filter(c => c.classList.contains('priority-slot'))
          .map(c => c.getBoundingClientRect())
      : null;

    ghostOffset.current = {
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top
    };

    const clone = targetElement.cloneNode(true);
    clone.style.position = 'fixed';
    clone.style.top = '0';
    clone.style.left = '0';
    clone.style.margin = '0';
    clone.style.width = `${rect.width}px`;
    clone.style.height = `${rect.height}px`;
    clone.style.boxSizing = 'border-box';
    clone.style.pointerEvents = 'none'; // der Ghost darf nie Touch-/Klick-Ziel sein
    clone.style.zIndex = '1000';
    clone.style.opacity = '1';
    clone.classList.remove('dragging'); // never inherit the dimmed placeholder look
    // Drive position purely via transform (GPU) for smooth tracking.
    clone.style.transform = `translate3d(${rect.left}px, ${rect.top}px, 0) scale(1.05)`;
    clone.style.willChange = 'transform';

    clone.classList.add('touch-drag-ghost');

    // In den Scanner-Container hängen (nicht an den body), damit die unter
    // .scanner3-container gescopten Slot-Styles den Klon weiterhin treffen.
    // Der Container ist position:fixed über den ganzen Viewport — die
    // fixed-Koordinaten des Ghosts bleiben dadurch unverändert.
    const scannerRoot = targetElement.closest('.scanner3-container') || document.body;
    scannerRoot.appendChild(clone);
    ghostRef.current = clone;
  };

  const handleTouchMove = (e) => {
    if (!draggedJob) return;
    // Nur der Finger zählt, der den Drag gestartet hat.
    const touch = Array.from(e.changedTouches).find(t => t.identifier === touchIdRef.current);
    if (!touch) return;
    // With touch-action:none on the slots the browser won't scroll, but guard anyway.
    if (e.cancelable) e.preventDefault();

    latestPointRef.current = { x: touch.clientX, y: touch.clientY };

    // Throttle ghost movement + reorder checks to one per animation frame.
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      const point = latestPointRef.current;
      if (!point) return;

      moveGhost(point.x, point.y);

      // Treffer-Slot über die gecachten Rects bestimmen — kein
      // elementFromPoint/getBoundingClientRect mehr pro Frame.
      const rects = slotRectsRef.current;
      if (!rects) return;
      const slotIndex = rects.findIndex(r =>
        point.x >= r.left && point.x <= r.right && point.y >= r.top && point.y <= r.bottom);
      if (slotIndex !== -1) {
        reorderToSlot(point.x, slotIndex, rects[slotIndex]);
      }
    });
  };

  // Touch-Ende nur akzeptieren, wenn wirklich der Drag-Finger abgehoben hat —
  // das Abheben eines zweiten Fingers darf den Drag nicht vorzeitig beenden.
  const handleTouchEnd = (e) => {
    if (touchIdRef.current === null) return;
    const ended = Array.from(e.changedTouches).some(t => t.identifier === touchIdRef.current);
    if (!ended) return;
    endDrag();
  };

  const handleSubmitWishes = () => {
    if (!scannedChild || !selectedEinheit || selectedJobs.length < 1) return;

    const priorities = [50, 40, 30, 20, 10];
    const requests = selectedJobs.map((job, index) => ({
        EinheitID: selectedEinheit,
        KindID: scannedChild.KindID,
        JobID: job.JobID,
        Prioritaet: priorities[index],
    }));

    if (requests.length === 0) {
      alert("Bitte wähle mindestens einen Job aus.");
      return;
    }

    setIsLoading(true);
    fetch(`${host_url}/kinderwuensche`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requests),
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(text => { throw new Error(text) });
        }
        return response.json();
      })
      .then(() => {
        setPage(3); // Go to success page
        setIsLoading(false);
      })
      .catch(error => {
        if (error.message.includes('ER_DUP_ENTRY')) {
          setError('Für dieses Kind wurden in dieser Einheit schon Wünsche abgesendet. Bitte einen Betreuer um Hilfe.');
        } else {
          try {
            const errorJson = JSON.parse(error.message);
            if (errorJson.fatal) {
              setError('Ein schwerwiegender Server-Fehler ist aufgetreten. Bitte informiere einen Betreuer.');
            } else {
              setError('Ein Fehler ist aufgetreten: ' + (errorJson.message || error.message));
            }
          } catch (e) {
            setError('Ein Fehler ist aufgetreten: ' + error.message);
          }
        }
        setIsLoading(false);
      });
  };

  const renderCancelConfirmDialog = () => (
    <div className="cancel-confirm-overlay">
        <div className="cancel-confirm-dialog">
            <h2>Abbrechen?</h2>
            <p>Willst du wirklich abbrechen? Deine Auswahl geht verloren.</p>
            <div className="dialog-buttons">
                <button onClick={() => setShowCancelConfirm(false)} className="dialog-button-no">Nein, weiter</button>
                <button onClick={handleLogout} className="dialog-button-yes">Ja, abbrechen</button>
            </div>
        </div>
    </div>
);

  // UI Rendering
  const handleLogoClick = () => {
    if (isPlaying) return;
    setGifKey(Date.now());
    setIsPlaying(true);
    setTimeout(() => {
      setIsPlaying(false);
    }, ANIMATION_DURATION_MS);
  };

  const renderScanPage = () => (
    <div className="scan-prompt">
      <img 
        src={isPlaying ? `${storminiGif}?t=${gifKey}` : (gifFirstFrame || "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7")} 
        alt="Stormini" 
        className="scan-logo" 
        onClick={handleLogoClick}
      />
      <h1>Scanne deinen Barcode</h1>
      <p className="scan-subtitle">Halte deinen Ausweis vor den Scanner</p>
      <span className="material-symbols-outlined scanner-icon">barcode_scanner</span>
    </div>
  );

  // Schichtbeginn als Date (Datum + Start-Uhrzeit; Fallback über den Namen:
  // Vormittag 10:00, Nachmittag 14:00).
  const getEinheitStart = (einheit) => {
    const start = new Date(einheit.Datum);
    const fallback = einheit.Name && einheit.Name.endsWith('Nachmittag') ? '14:00:00' : '10:00:00';
    const [h, m] = (einheit.Start || fallback).split(':');
    start.setHours(Number(h), Number(m), 0, 0);
    return start;
  };

  // Bewerbungsfenster — Aktiv-Flag hat drei Zustände:
  //   0 = manuell gesperrt (nie bewerbbar)
  //   1 = Automatik: offen ab 24 h vor Schichtbeginn, zu ab Beginn der Einheit
  //   2 = manuell geöffnet: Kernteam übersteuert das Zeitfenster (z. B. um eine
  //       bereits begonnene Einheit wieder für Bewerbungen zu öffnen)
  const isBewerbbar = (einheit) => {
    if (einheit.Aktiv === 0) return false;
    if (einheit.Aktiv === 2) return true;
    const start = getEinheitStart(einheit).getTime();
    const now = Date.now();
    return now >= start - 24 * 60 * 60 * 1000 && now < start;
  };

  // Gruppierung nur nach Wochentag: Das Terminal läuft genau eine Camp-Woche,
  // jeder Wochentag ist also eindeutig — so sind unabhängig vom konkreten
  // Datum (auch bei Testdaten) immer alle Tage sichtbar und wählbar.
  // Memoisiert, damit die Gruppierung (inkl. toLocaleDateString pro Einheit)
  // nicht bei jedem Render neu läuft, sondern nur wenn sich einheiten ändert.
  const groupedEinheiten = useMemo(() => einheiten.reduce((acc, einheit) => {
    const weekday = new Date(einheit.Datum).toLocaleDateString('de-DE', { weekday: 'long' });
    if (!acc[weekday]) {
      acc[weekday] = [];
    }
    acc[weekday].push(einheit);
    return acc;
  }, {}), [einheiten]);

  const renderApplicationPage = () => {
    if (!scannedChild) return null;

    return (
      <div className="bewerbung-form" onPointerDown={startOrResetInactivityTimer}>
        {showCancelConfirm && renderCancelConfirmDialog()}
        {/* "Hallo XYZ" nur bei der Schicht-Auswahl — auf der Job-Auswahl braucht der Scrollbereich die volle Höhe */}
        {selectedEinheit === '' && <h1>Hallo {scannedChild.Name}!</h1>}

        {selectedEinheit === '' ? (
          <>
            <p>Wähle eine Schicht.</p>
            <div className="einheiten-grid">
              {Object.entries(groupedEinheiten).map(([date, einheitenOnDay]) => (
                <div key={date} className="einheiten-day-group">
                  <h4>{date}</h4>
                  <div className="einheiten-buttons">
                    {einheitenOnDay.map(einheit => {
                      const offen = isBewerbbar(einheit);
                      return (
                        <button
                          key={einheit.ID}
                          className={`einheit-button ${offen ? '' : 'inactive'}`}
                          onClick={() => setSelectedEinheit(einheit.ID)}
                          disabled={!offen}
                        >
                          {einheit.Name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <div className="form-actions">
                <button onClick={() => setShowCancelConfirm(true)} className="logout-button">Zurück</button>
            </div>
          </>
        ) : (
          <div className="job-selection-view">
            <div className="sticky-header">
              <div className="einheit-selection-header">
                  <button onClick={() => setSelectedEinheit('')} className="back-to-einheit-button"><span className="material-symbols-outlined">arrow_back</span></button>
                  <h2>{einheiten.find(e => e.ID === selectedEinheit)?.Name}</h2>
              </div>

              <div className="priority-slots-header">
                <h3>Wähle 5 Jobs aus und sortiere sie nach Priorität – 1 ist dein Top-Favorit</h3>
                <p>{selectedJobs.length} von 5 Wünschen ausgewählt</p>
              </div>
              <div className="priority-slots-container" ref={containerRef}>
                {[...Array(5)].map((_, index) => {
                  const job = selectedJobs[index];
                  if (job) {
                    return (
                      <div
                        key={job.JobID}
                        data-job-id={job.JobID}
                        className={`priority-slot filled ${draggedJob && draggedJob.JobID === job.JobID ? 'dragging' : ''}`}
                        draggable
                        onDragStart={(e) => handleDragStart(e, job)}
                        onDragOver={handleDragOver}
                        onDragEnter={(e) => handleDragEnter(e, job)}
                        onDrop={endDrag}
                        onDragEnd={endDrag}
                        onTouchStart={(e) => handleTouchStart(e, job)}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                        onTouchCancel={handleTouchEnd}
                      >
                        <span className="priority-slot-number">{index + 1}</span>
                        <span className="priority-slot-name">{job.JobName}{job.Handwerksjob ? <span className="material-symbols-outlined" style={{ fontSize: 'inherit', verticalAlign: 'middle', marginLeft: '0.3em' }}>handyman</span> : ''}</span>
                        <span className="drag-handle">≡</span>
                      </div>
                    );
                  } else {
                    return (
                      <div key={index} className="priority-slot empty">
                        <span className="priority-slot-number">{index + 1}</span>
                        <span className="priority-slot-name">Noch frei</span>
                      </div>
                    );
                  }
                })}
              </div>
            </div>

            <div className="scrollable-content">
              {isLoading && <p className="loading-text">Lade Jobs...</p>}
              {!isLoading && (
                  <div className="job-tiles-grid">
                    {filteredJobs.map(job => {
                      const selectionIndex = selectedJobs.findIndex(selected => selected.JobID === job.JobID);
                      const isSelected = selectionIndex !== -1;
                      const isDisabled = !isSelected && selectedJobs.length === 5;

                      return (
                        <div
                          key={job.JobID}
                          className={`job-tile ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                          onClick={() => !isDisabled && handleJobSelect(job)}
                        >
                          {isSelected && <span className="priority-badge">{selectionIndex + 1}</span>}
                          <h3 className="job-name">{job.JobName}{job.Handwerksjob ? <span className="material-symbols-outlined" style={{ fontSize: 'inherit', verticalAlign: 'middle', marginLeft: '0.3em' }}>handyman</span> : ''}</h3>
                          <p className="job-slots">Plätze frei: {job.Slots}</p>
                        </div>
                      );
                    })}
                  </div>
              )}
            </div>

            <div className="sticky-footer">
              <div className="form-actions">
                <button onClick={() => setShowCancelConfirm(true)} className="logout-button">Abbrechen</button>
                <button onClick={handleSubmitWishes} className="submit-button" disabled={selectedJobs.length < 1 || isLoading}>
                  Bewerbung absenden
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };


  const renderSuccessPage = () => {
    // Nach einer Vormittagsbewerbung anbieten, sich direkt auch für die
    // Nachmittagsschicht zu bewerben — nur wenn es am selben Tag eine gibt
    // und sie gerade bewerbbar ist (Mittwoch/Freitag haben keinen Nachmittag).
    const beworbeneEinheit = einheiten.find(e => e.ID === selectedEinheit);
    const nachmittagsEinheit =
      beworbeneEinheit && beworbeneEinheit.Name && beworbeneEinheit.Name.endsWith('Vormittag')
        ? einheiten.find(e =>
            e.Name && e.Name.endsWith('Nachmittag') &&
            new Date(e.Datum).toDateString() === new Date(beworbeneEinheit.Datum).toDateString() &&
            isBewerbbar(e))
        : null;

    return (
      <div className="success-message">
        <span className="material-symbols-outlined success-check">check_circle</span>
        <h2>Bewerbung<br />erfolgreich abgeschickt!</h2>
        {nachmittagsEinheit
          ? <p>Willst du dich auch für die Nachmittagsschicht bewerben?</p>
          : <p>Du kannst dich jetzt abmelden.</p>}
        <div className="success-actions">
          {nachmittagsEinheit && (
            <button onClick={handleNextApplication} className="submit-button">Ja, weiter bewerben</button>
          )}
          <button onClick={handleLogout} className="logout-button">Abmelden</button>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (error) return (
      <div className="error-message">
        <h1>Fehler</h1>
        <p>{error}</p>
        <button onClick={handleLogout} className="logout-button">Erneut versuchen</button>
      </div>
    );

    switch (page) {
      case 2:
        return renderApplicationPage();
      case 3:
        return renderSuccessPage();
      case 1:
      default:
        return renderScanPage();
    }
  };

  return (
    <div className="scanner3-container">
      {page !== 1 && (
        <BackArrow
          to="/terminal"
          label="Abmelden"
          icon="logout"
          onBeforeNavigate={() => { handleLogout(); return false; }}
        />
      )}
      <TimeoutCounter active={page >= 2} timerEndRef={timerEndRef} />
      <div className="scanner-content-wrapper">
        {renderContent()}
      </div>
    </div>
  );
}

export default Scanner;
